import React from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Truck, TriangleAlert } from "lucide-react";
import PrettyDate from "components/format/PrettyDate";
import {
  Delivery,
  DeliveryWarning,
  parseDeliveryExternalMeta,
} from "types/Delivery";

export type TFunction = (key: string, options?: any) => string;

export interface DeliveryField {
  key: string;
  label: string;
  value: React.ReactNode;
}

interface TerminalasDeliveryDetailsProps {
  delivery: Delivery;
}

const WARNING_LABEL_KEYS: Record<string, string> = {
  OVER_RESERVED: "deliveries_terminalas_warning_over_reserved",
  OVER_ORDERED: "deliveries_terminalas_warning_over_ordered",
  PRODUCT_UNKNOWN: "deliveries_terminalas_warning_product_unknown",
  PRODUCT_NOT_IN_CONTRACT: "deliveries_terminalas_warning_product_not_in_contract",
  PRODUCT_NOT_IN_RESERVATION: "deliveries_terminalas_warning_product_not_in_reservation",
  ORDER_NOT_SELLABLE: "deliveries_terminalas_warning_order_not_sellable",
};

export function warningText(t: TFunction, warning: DeliveryWarning): string {
  const label = WARNING_LABEL_KEYS[warning.code]
    ? t(WARNING_LABEL_KEYS[warning.code])
    : warning.code;
  const details: string[] = [];
  if (warning.sku) details.push(warning.sku);
  if (warning.reserved !== undefined) {
    details.push(`${t("deliveries_reserved")}: ${warning.reserved}`);
  }
  if (warning.ordered !== undefined) {
    details.push(`${t("ordered")}: ${warning.ordered}`);
  }
  if (warning.shipped !== undefined) {
    details.push(`${t("shipped")}: ${warning.shipped}`);
  }
  return details.length > 0 ? `${label} (${details.join(", ")})` : label;
}

/** Warnings the terminal sync attached to a delivery (empty when none). */
export function getDeliveryWarnings(delivery: Delivery): DeliveryWarning[] {
  return parseDeliveryExternalMeta(delivery.externalMeta).warnings || [];
}

/**
 * The truck fields of a terminalas delivery that have a value, in display
 * order. `formatTonnes` lets a caller show the net weight in its own number
 * format; the default is the plain three-decimal form.
 */
export function buildTerminalasFields(
  t: TFunction,
  delivery: Delivery,
  formatTonnes: (tonnes: number) => string = (tonnes) => tonnes.toFixed(3)
): DeliveryField[] {
  const netWeightTonnes =
    typeof delivery.netWeightKg === "number"
      ? formatTonnes(delivery.netWeightKg / 1000)
      : null;

  const fields: DeliveryField[] = [
    { key: "plate", label: t("deliveries_terminalas_plate"), value: delivery.vehiclePlate },
    { key: "driver", label: t("deliveries_terminalas_driver"), value: delivery.driverName },
    { key: "carrier", label: t("deliveries_terminalas_carrier"), value: delivery.carrierName },
    {
      key: "loadedAt",
      label: t("deliveries_terminalas_loaded_at"),
      value: delivery.loadedAt ? (
        <PrettyDate dateString={delivery.loadedAt} showTime />
      ) : null,
    },
    {
      key: "gateExitAt",
      label: t("deliveries_terminalas_gate_exit_at"),
      value: delivery.gateExitAt ? (
        <PrettyDate dateString={delivery.gateExitAt} showTime />
      ) : null,
    },
    {
      key: "netWeight",
      label: t("deliveries_terminalas_net_weight"),
      value: netWeightTonnes !== null ? `${netWeightTonnes} t` : null,
    },
  ];
  return fields.filter(
    (field) => field.value !== null && field.value !== undefined && field.value !== ""
  );
}

/**
 * Truck / shipment details for a delivery synced from terminalas
 * (`delivery.source === 'terminalas'`) — rendered inside DeliveryCard's
 * existing Polaris shell in place of the manual-entry edit/delete controls,
 * which do not apply: these rows are read-only (see the backend's
 * DELIVERY_MANAGED_BY_TERMINALAS guard).
 */
export default function TerminalasDeliveryDetails({
  delivery,
}: TerminalasDeliveryDetailsProps) {
  const { t } = useTranslation();
  const warnings = getDeliveryWarnings(delivery);
  const fields = buildTerminalasFields(t, delivery);

  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 text-sm dark:border-zinc-700 dark:bg-zinc-800/40">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-sky-500/15 px-1.5 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-500/10 dark:text-sky-400">
          <Truck className="size-3.5" aria-hidden="true" />
          {t("deliveries_terminalas_badge")}
        </span>
        {delivery.externalUrl ? (
          <a
            href={delivery.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
          >
            {delivery.externalOrderCode || t("deliveries_terminalas_order_code")}
            <ExternalLink className="size-3" aria-hidden="true" />
          </a>
        ) : delivery.externalOrderCode ? (
          <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">
            {delivery.externalOrderCode}
          </span>
        ) : null}
      </div>

      {fields.length > 0 && (
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
          {fields.map((field) => (
            <div key={field.key}>
              <dt className="text-xs text-zinc-500 dark:text-zinc-400">{field.label}</dt>
              <dd className="font-medium text-zinc-900 dark:text-zinc-100">{field.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {warnings.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-zinc-200 pt-2 dark:border-zinc-700">
          {warnings.map((warning, index) => (
            <li
              key={`${warning.code}-${index}`}
              className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"
            >
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
              {warningText(t, warning)}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
        {t("deliveries_terminalas_managed_notice")}
      </p>
    </div>
  );
}
